import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter, formatCreatedBy } from "src/utils/common";
import { formatAddress } from "src/utils/formatAddress";
import supplierApis from "src/api/supplier";
import purchaseOrderApis from "src/api/purchaseOrder";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const SupplierDetailsPage: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState<any>(null);
  const [orderList, setOrderList] = useState<any>(null);

  const fetchSupplier = async (id: string) => {
    const response = await supplierApis.getSupplierDetails(id);
    return response?.data;
  };

  const fetchOrders = async (page = 1) => {
    const response = await purchaseOrderApis.list({
      page,
      limit: 10,
      supplierId,
    });
    setOrderList(response);
  };

  useEffect(() => {
    const loadData = async () => {
      if (!supplierId) return;
      setLoading(true);
      try {
        const supplierData = await fetchSupplier(supplierId);
        setSupplier(supplierData);
        await fetchOrders(1);
      } catch (error) {
        console.error("Error fetching supplier details:", error);
        toast.error("Failed to load supplier details");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [supplierId]);

  const supplierStatusBadge = useMemo(() => {
    if (!supplier?.status) return null;
    return (
      <Badge
        text={supplier.status}
        color={supplier.status === "active" ? "green" : "red"}
      />
    );
  }, [supplier?.status]);

  const handlePageChange = (page: number) => {
    fetchOrders(page);
  };

  return (
    <DashboardLayout
      meta={{
        title: "Supplier Details | Invyntra",
        description: "View supplier profile and purchase order history.",
        keywords: "supplier, purchase orders, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Supplier Details"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Suppliers", path: "/app/supplier/list" },
            { label: supplier?.name || "Details", path: "" },
          ]}
          showBack
          // onBack={() => navigate(-1)}
        />

        {loading ? (
          <Card>
            <div className="py-10 text-center text-sm text-gray-600">
              Loading supplier details...
            </div>
          </Card>
        ) : !supplier ? (
          <Card>
            <div className="py-10 text-center text-sm text-gray-600">
              Supplier not found.
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-gray-500">Supplier</p>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {supplier.name}
                  </h2>
                  <p className="text-sm text-gray-600">
                    ID: {supplier.supplierId}
                  </p>
                </div>
                {supplierStatusBadge}
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2 text-sm text-gray-700">
                <div>
                  <span className="text-gray-500">Contact Person: </span>
                  {supplier.contactPerson || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Phone: </span>
                  {supplier.phone || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Email: </span>
                  {supplier.email || "-"}
                </div>
                <div>
                  <span className="text-gray-500">GST: </span>
                  {supplier.gstNumber || "-"}
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-500">Address: </span>
                  {formatAddress(supplier) || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Created By: </span>
                  {formatCreatedBy(supplier.createdBy)}
                </div>
                <div>
                  <span className="text-gray-500">Created On: </span>
                  {formatDateTime(supplier.createdAt, "DD/MM/YYYY")}
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Purchase Order History
                </h3>
                <span className="text-xs text-gray-500">
                  {orderList?.pagination?.totalDocs || 0} orders
                </span>
              </div>
              <div className="mt-4">
                <TableV2
                  columns={[
                    {
                      key: "purchaseOrderId",
                      header: "Order",
                      accessor: ({ row }) => (
                        <Link
                          to={`/app/purchase/${row._id}`}
                          className="text-primary-600 hover:underline"
                        >
                          #{row.purchaseOrderId}
                        </Link>
                      ),
                    },
                    {
                      key: "grandTotal",
                      header: "Total",
                      align: "right",
                      accessor: ({ row }) =>
                        currencyFormatter(row.grandTotal || 0),
                    },
                    {
                      key: "paymentStatus",
                      header: "Payment",
                      accessor: ({ row }) => (
                        <Badge
                          text={row.paymentStatus}
                          color={
                            row.paymentStatus === "paid"
                              ? "green"
                              : row.paymentStatus === "failed"
                              ? "red"
                              : "yellow"
                          }
                        />
                      ),
                    },
                    {
                      key: "orderStatus",
                      header: "Status",
                      accessor: ({ row }) => (
                        <Badge
                          text={row.orderStatus}
                          color={
                            row.orderStatus === "completed" ? "green" : "yellow"
                          }
                        />
                      ),
                    },
                    {
                      key: "createdAt",
                      header: "Date",
                      accessor: ({ row }) =>
                        formatDateTime(row.createdAt, "DD/MM/YYYY"),
                    },
                  ]}
                  data={orderList?.data || []}
                  pagination={orderList?.pagination}
                  onPageChange={handlePageChange}
                />
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SupplierDetailsPage;
